import { faImage, faTrophy } from "@fortawesome/pro-solid-svg-icons"
import { Box, Grid, IconButton, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { StandardChip } from "../../../../shared/components/badges/StandardChip"
import { ProductTableRow } from "./productsTypes"
import { StockChip } from "./components/stockManagement/StockChip"
import { tailwindPalette } from "../../../theme/tailwindPalette"
import { MatchEnvironment } from "../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../environment/envTypes"

const useStyles = makeStyles()(() => ({
    soldTodayLabel: {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
        borderRadius: "12px",
        padding: "4px 8px",
        whiteSpace: "nowrap",
        color: tailwindPalette.slate[700],
        border: `1px solid ${tailwindPalette.slate[100]}`,
    },
    soldTodayLabelDoingWell: {
        color: tailwindPalette.green[700],
        border: `1px solid ${tailwindPalette.green[100]}`,
    },
}))

type ProductGroupItemBoxProps = Readonly<{
    productGroup: ProductTableRow
}>

export function ProductGroupItemBox({ productGroup }: ProductGroupItemBoxProps) {
    return (
        <Typography variant="body1" fontWeight="bold">
            {productGroup.name}
        </Typography>
    )
}

type ProductNameProps = Readonly<{
    product: ProductTableRow
    onProductPhotoClick: (imageUrl: string) => void
    onStockChipClick?: (event: React.MouseEvent<HTMLElement>, productId: number) => void
    activeStockProductId?: number | null
}>

export function ProductName({
    product,
    onProductPhotoClick,
    onStockChipClick,
    activeStockProductId,
}: ProductNameProps) {
    const { classes, cx } = useStyles()
    const productPhotoUrl = product.productPhoto?.url

    return (
        <Grid container>
            <Grid container item gap="1rem" alignItems="center" wrap="nowrap">
                <Grid item>{product.name}</Grid>
                {productPhotoUrl && (
                    <Grid item>
                        <IconButton onClick={() => onProductPhotoClick(productPhotoUrl)} sx={{ padding: 0 }}>
                            <FontAwesomeIcon icon={faImage} fontSize="16px" color="#94A3B8" />
                        </IconButton>
                    </Grid>
                )}
                {product.marketingDetails?.isOffer && (
                    <Grid item>
                        <LocalizedStrict id="product-offer-chip" attrs={{ label: true }}>
                            <StandardChip size="small" label="Offer" variant="yellow" placement="table" />
                        </LocalizedStrict>
                    </Grid>
                )}
                <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}>
                    {product.type === "product" && product.stock && (
                        <Grid item>
                            <StockChip
                                stock={product.stock}
                                isOpen={activeStockProductId === product.id}
                                onClick={(e) => onStockChipClick?.(e, product.id)}
                            />
                        </Grid>
                    )}
                    {product.type === "product" &&
                        product.stock?.sales?.daily != null &&
                        !!product.stock.sales.daily.itemsForSale &&
                        (() => {
                            const total = product.stock.sales.daily.itemsForSale
                            const sold = product.stock.sales.daily.sold ?? 0
                            const isDoingWell = total > 0 && sold / total >= 0.8

                            return (
                                <Grid item>
                                    <Box
                                        component="span"
                                        className={cx(
                                            classes.soldTodayLabel,
                                            isDoingWell && classes.soldTodayLabelDoingWell,
                                        )}
                                    >
                                        {isDoingWell && (
                                            <FontAwesomeIcon
                                                icon={faTrophy}
                                                style={{ width: "1rem", height: "0.875rem" }}
                                                color={tailwindPalette.amber[500]}
                                            />
                                        )}
                                        <LocalizedStrict id="product-stock-daily-sales" vars={{ sold, total }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                            >{`Sold today: ${sold}/${total}`}</Typography>
                                        </LocalizedStrict>
                                    </Box>
                                </Grid>
                            )
                        })()}
                </MatchEnvironment>
            </Grid>
        </Grid>
    )
}
